import { useState, type ReactNode } from 'react'
import { HoverHelp } from '@/tableControls'
import type { MemberAmounts, ProposalVestingItem } from '@/types/waterfall'
import { MEMBERS, MEMBER_LABELS } from '@/types/waterfall'
import { formatCurrencyField } from '@/utils/waterfallCalculator'
import { parseCellInput } from '@/utils/waterfallGrid'
import {
  isCatchUpReady,
  loadVestingCatchUpThreshold,
  saveVestingCatchUpThreshold,
  splitCatchUp,
} from '@/utils/vestingCatchup'
import type { SplitTableWidths } from '@/utils/splitTableLayout'

const fmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

const SPLIT_LABEL_HEAD =
  'sticky left-0 z-20 bg-gray-50 border-r border-gray-200 px-3 py-2 max-md:px-2 text-left font-medium text-gray-600 whitespace-nowrap'
const SPLIT_LABEL_CELL =
  'sticky left-0 z-10 bg-white group-hover:bg-gray-50 border-r border-gray-200 px-3 py-2 max-md:px-2 text-left whitespace-nowrap'
const SPLIT_NUM_HEAD = 'px-3 py-2 max-md:px-2 text-right font-medium text-gray-600 whitespace-nowrap'
const SPLIT_NUM_CELL = 'px-3 py-2 max-md:px-2 text-right tabular-nums whitespace-nowrap'
const SPLIT_TOTAL_HEAD = `${SPLIT_NUM_HEAD} border-l border-gray-300`
const SPLIT_TOTAL_CELL = `${SPLIT_NUM_CELL} border-l border-gray-300 font-medium`

function CodeTerm({ children }: { children: ReactNode }) {
  return (
    <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[11px] text-gray-800">
      {children}
    </code>
  )
}

function SplitTableColgroup({ widths }: { widths: SplitTableWidths }) {
  return (
    <colgroup>
      <col style={{ width: widths.labelPx }} />
      {MEMBERS.map((m) => (
        <col key={m} style={{ width: widths.valuePx }} />
      ))}
      <col style={{ width: widths.valuePx }} />
    </colgroup>
  )
}

interface Props {
  items: ProposalVestingItem[]
  catchUpAmounts: MemberAmounts
  onCatchUpChange: (amounts: MemberAmounts) => void
  catchUpSeparate: boolean
  onCatchUpSeparateChange: (separate: boolean) => void
  splitTableWidths: SplitTableWidths | null
  splitTableMinWidth: number
  disabled?: boolean
}

export default function VestingCatchUpPanel({
  items,
  catchUpAmounts,
  onCatchUpChange,
  catchUpSeparate,
  onCatchUpSeparateChange,
  splitTableWidths,
  splitTableMinWidth,
  disabled,
}: Props) {
  const [threshold, setThreshold] = useState(loadVestingCatchUpThreshold)
  const [thresholdFocused, setThresholdFocused] = useState(false)
  const [thresholdDraft, setThresholdDraft] = useState('')
  const [editingMember, setEditingMember] = useState<(typeof MEMBERS)[number] | null>(null)
  const [memberDraft, setMemberDraft] = useState('')

  if (items.length === 0) return null

  const itemByMember = Object.fromEntries(items.map((a) => [a.member, a]))
  const catchUpTotal = MEMBERS.reduce((sum, m) => sum + catchUpAmounts[m], 0)

  const updateCatchUp = (member: (typeof MEMBERS)[number], raw: string) => {
    const item = itemByMember[member]
    if (!item) return
    const parsed = parseCellInput(raw)
    if (parsed === null) return
    const clamped = Math.min(Math.max(parsed, 0), item.pro_forma_payable)
    onCatchUpChange({ ...catchUpAmounts, [member]: clamped })
  }

  const updateThreshold = (raw: string) => {
    const parsed = parseCellInput(raw)
    if (parsed === null) return
    const v = Math.max(0, parsed)
    setThreshold(v)
    saveVestingCatchUpThreshold(v)
  }

  return (
    <section className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
      <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-end justify-between gap-3 max-md:flex-col max-md:items-stretch">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Vesting catch-up</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            Pay any amount up to Payable now (pro forma). Scott&apos;s vested share of each ordinary
            distribution is included here; whatever you don&apos;t pay is withheld and accrues to
            Distributions Payable. His unvested share goes to LVW/TR.
          </p>
        </div>
        <label className="text-xs text-gray-600 flex flex-wrap items-center gap-2 max-md:w-full">
          Notify when <CodeTerm>Payable now</CodeTerm> ≥
          <input
            type="text"
            inputMode="decimal"
            className="w-28 max-md:w-full rounded border border-gray-300 px-2 py-1 text-sm tabular-nums text-right"
            value={
              thresholdFocused
                ? thresholdDraft
                : formatCurrencyField(threshold) || '$0.00'
            }
            placeholder="$0.00"
            onFocus={() => {
              setThresholdFocused(true)
              setThresholdDraft(
                threshold === 0 ? '' : formatCurrencyField(threshold),
              )
            }}
            onChange={(e) => setThresholdDraft(e.target.value)}
            onBlur={() => {
              updateThreshold(thresholdDraft)
              setThresholdFocused(false)
            }}
          />
        </label>
      </div>

      <div className="p-4 space-y-4">
        {items.map((item) => {
          const ready = isCatchUpReady(item.pro_forma_payable, threshold)
          const label =
            MEMBER_LABELS[item.member as keyof typeof MEMBER_LABELS] ?? item.member
          const C = catchUpAmounts[item.member as (typeof MEMBERS)[number]] ?? 0
          const { c1, c2 } = splitCatchUp(C, item.this_distribution)
          const poolSuffix =
            item.last_pool_event_num != null && item.last_pool_event_date
              ? ` since #${item.last_pool_event_num} (${item.last_pool_event_date})`
              : item.last_pool_event_num != null
                ? ` since #${item.last_pool_event_num}`
                : item.last_pool_event_date
                  ? ` since ${item.last_pool_event_date}`
                  : ''
          return (
            <article
              key={item.member}
              className={`rounded-lg border p-4 ${
                ready ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200 bg-gray-50/40'
              }`}
            >
              <header className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <p className="text-xs text-gray-500">{label}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    <HoverHelp label="Payable now (pro forma)" aria-label="Payable now pro forma help">
                      <p>
                        Vested and unpaid from prior distributions plus Scott&apos;s vested share of
                        this distribution&apos;s Class B pools. Pay any part of it here; the rest is
                        withheld and accrued to Distributions Payable.
                      </p>
                    </HoverHelp>
                    <span className="ml-1 tabular-nums font-semibold">
                      {fmt.format(item.pro_forma_payable)}
                    </span>
                  </p>
                </div>
              </header>

              {item.pro_forma_payable <= 0.005 ? (
                <p className="text-xs text-gray-600">
                  Nothing payable — no Class B (Tier 3/4) cash{poolSuffix} and none in this
                  distribution.
                </p>
              ) : (
                <p className="text-xs text-gray-600">
                  from prior distributions {fmt.format(item.prior_payable)} (funded by payable{' '}
                  {fmt.format(item.withheld_balance)}, new cash {fmt.format(item.from_new_cash)}) ·
                  from this distribution {fmt.format(item.this_distribution)}
                </p>
              )}

              {C > 0.005 && (
                <p className="mt-2 text-xs text-gray-600">
                  {fmt.format(c1)} paid from this distribution&apos;s pool · {fmt.format(c2)} paid
                  against the payable
                </p>
              )}
            </article>
          )
        })}

        {splitTableWidths && (
          <div className="table-scroll-mobile overflow-x-auto overscroll-x-contain">
            <table
              className="w-full table-fixed text-sm border-separate border-spacing-0"
              style={{ minWidth: splitTableMinWidth }}
            >
              <SplitTableColgroup widths={splitTableWidths} />
              <thead className="bg-gray-50">
                <tr>
                  <th className={SPLIT_LABEL_HEAD} />
                  {MEMBERS.map((m) => (
                    <th key={m} className={SPLIT_NUM_HEAD}>
                      {m}
                    </th>
                  ))}
                  <th className={SPLIT_TOTAL_HEAD}>Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className={`${SPLIT_LABEL_CELL} text-gray-600`}>Payable now (pro forma)</td>
                  {MEMBERS.map((m) => {
                    const item = itemByMember[m]
                    return (
                      <td key={m} className={`${SPLIT_NUM_CELL} text-gray-600`}>
                        {item && item.pro_forma_payable > 0.005
                          ? fmt.format(item.pro_forma_payable)
                          : '—'}
                      </td>
                    )
                  })}
                  <td className={SPLIT_TOTAL_CELL}>
                    {fmt.format(
                      MEMBERS.reduce(
                        (sum, m) => sum + (itemByMember[m]?.pro_forma_payable ?? 0),
                        0,
                      ),
                    )}
                  </td>
                </tr>
                <tr className="bg-brand-50/40">
                  <td className={`${SPLIT_LABEL_CELL} bg-brand-50 font-medium text-brand-900`}>
                    <div className="flex flex-col items-start gap-2">
                      <HoverHelp label="Catch-up payment" aria-label="Catch-up payment help">
                        <p>
                          {catchUpSeparate
                            ? 'Catch-up logged as a separate distribution. Shown in the Catch-up payment row below; the Actual payment row stays unchanged.'
                            : 'Catch-up included in the Actual payment row below when you log this distribution.'}
                        </p>
                      </HoverHelp>
                      {catchUpTotal > 0.005 && (
                        <div
                          className="backfill-audit-segmented-toggle inline-flex rounded-lg bg-gray-200 p-0.5 font-normal"
                          role="group"
                          aria-label="Catch-up distribution mode"
                        >
                          <button
                            type="button"
                            disabled={disabled}
                            className={`whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                              !catchUpSeparate
                                ? 'bg-white text-brand-700 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                            onClick={() => onCatchUpSeparateChange(false)}
                          >
                            This distribution
                          </button>
                          <button
                            type="button"
                            disabled={disabled}
                            className={`whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                              catchUpSeparate
                                ? 'bg-white text-brand-700 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                            onClick={() => onCatchUpSeparateChange(true)}
                          >
                            Separate
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                  {MEMBERS.map((m) => {
                    const item = itemByMember[m]
                    const canEdit = item && item.pro_forma_payable > 0.005 && !disabled
                    return (
                      <td key={m} className={`${SPLIT_NUM_CELL} bg-brand-50/40 px-2 py-1`}>
                        {canEdit ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            value={
                              editingMember === m
                                ? memberDraft
                                : formatCurrencyField(catchUpAmounts[m])
                            }
                            placeholder="$0.00"
                            onFocus={() => {
                              setEditingMember(m)
                              setMemberDraft(
                                catchUpAmounts[m] === 0
                                  ? ''
                                  : formatCurrencyField(catchUpAmounts[m]),
                              )
                            }}
                            onChange={(e) => setMemberDraft(e.target.value)}
                            onBlur={() => {
                              updateCatchUp(m, memberDraft)
                              setEditingMember(null)
                            }}
                            className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-right text-sm tabular-nums"
                          />
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    )
                  })}
                  <td className={`${SPLIT_TOTAL_CELL} bg-brand-50/40`}>
                    {fmt.format(catchUpTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  )
}
